import { useAspectRatioDimensions } from "@/lib/dimensions";
import { useRef } from "react";
import AspectRatioControls from "./AspectRatioControls";
import VideoPreviewHelperText from "./VideoPreviewHelperText";
import VideoPreviewBanners from "./VideoPreviewBanners";
import VideoPreviewControls from "./VideoPreviewControls/VideoPreviewControls";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { SourceImageDto } from "@repo/api/source-image";

type Props = {
  targetWidth: number;
  targetHeight: number;
  children: React.ReactNode;
  timelineItems: CreativeProjectTimelineItem[];
  animatedImages: Record<string, AnimatedImageDto>;
  sourceImages: Record<string, SourceImageDto>;
  durationInFrames: number;
  fps: number;
};

// The player size must be calculated in pixels to keep the aspect ratio of the composition.
// Safari breaks if trying to use just flexbox for this.
export function PlayerWrapper({
  targetWidth,
  targetHeight,
  children,
  timelineItems,
  animatedImages,
  sourceImages,
  durationInFrames,
  fps,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const { width, height } = useAspectRatioDimensions(
    ref,
    targetWidth,
    targetHeight,
  );

  // Do not add any early returns here,
  // it will break the player ref in useAspectRatioDimensions.

  // The inner div is positioned absolutely so that it does not affect the size calculation
  // that uses the parent div's ref.
  return (
    <div ref={ref} className="relative flex-1">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative" style={{ width, height }}>
          <AspectRatioControls />
          {children}
          <VideoPreviewBanners timelineItems={timelineItems} />
          <VideoPreviewControls durationInFrames={durationInFrames} fps={fps} />
          <VideoPreviewHelperText
            timelineItems={timelineItems}
            animatedImages={animatedImages}
            sourceImages={sourceImages}
          />
        </div>
      </div>
    </div>
  );
}
